/*
 * The Southern Air Import — fixed monthly issue. See file history for the
 * full pass-by-pass log (eleventh pass now current) — short version: this
 * store's real card heights drift between measurements, sometimes by
 * hundreds of px with no picks change in between (a live store under
 * active edit). Every pass here re-measures live via getBoundingClientRect
 * on [data-m="sai-sidebar"]/[data-m="sai-adgrid"] rather than trusting the
 * previous pass's numbers, and expects the NEXT pass to do the same.
 *
 * Eleventh pass: looked at real late-'80s Soldier of Fortune issues
 * (archive.org) for how a real magazine's classified/mail-order pages
 * actually work. They're much denser than this issue's old size:11 per
 * spread — a dozen-plus small box ads, tightly packed — and denser pages
 * are exactly what makes multi-column's own balance algorithm close a
 * short column tighter (see assets/sat-import-pager-patch.js's PAGE1_ITEMS
 * comment: more, smaller pieces beat fewer, larger ones at making change).
 * Raised every single-column spread (no sidebar to balance against, so no
 * risk of re-triggering the sidebar-vs-grid drift the earlier passes chased)
 * from 11 to 16: Hasenfus, Mammoth, Reykjavik, Tax Reform, The Blade Room,
 * and the Closing page. K-219 and Vanunu (the two sidebar spreads) and
 * Page 1 are left alone here on purpose — raising their size re-opens the
 * exact cross-page drift the tenth pass just closed. The added items come
 * from each page's own alternates/reserve fallback (no new picks written
 * by hand this pass) — fine for a first look at the density change; worth
 * hand-curating specific picks for these slots once the higher density is
 * confirmed to actually help. Also added assets/sat-import-pager-patch.js's
 * saiIssueIndex (a real Advertiser's-Index-style page listing, built from
 * the same page-number math the pager already computes) — rendered on
 * Page 2, see snippets/sat-import.liquid.
 *
 * Twelfth pass: merchant feedback after the eleventh pass + the page-fit
 * (zoom) experiment: the zoom approach was the wrong fix entirely — it
 * shrank a page's real, readable size down to fit the viewport rather than
 * making the page itself shorter, which just made the text tiny instead of
 * cutting down the actual scroll. assets/sat-import-page-fit-patch.js is
 * removed (see sections/sat-app.liquid); the real fix is here — cut size
 * back down across the board so a spread's natural height roughly matches
 * one read-through without turning it into an endless scroll. Every
 * single-column spread dropped from 16 back to 10 (below the ELEVENTH
 * pass's 16, and below the ORIGINAL 11 too — the eleventh pass solved a
 * real blank-space problem but overcorrected on length; capHeroSpan's own
 * medium-first/small-last column-balance reorder, in
 * assets/sat-import-pager-patch.js, still does the real work of closing a
 * short column's gap at ANY item count, so 10 isn't expected to reopen the
 * blank-space problem the density bump was fixing). K-219/Vanunu (sidebar
 * spreads) cut from 16/19 to 11/12 — kept slightly fuller than the plain
 * spreads since their sidebar story column is narrower and the grid next to
 * it has more vertical room to fill before matching the sidebar's own
 * height. Page 1 cut from 18 to 12 — first landed with gridSize:10 (2 items
 * held back for a sidebar filler) on the assumption the grid was the long
 * side; live measurement showed the opposite (see the Page 1 entry below),
 * so that was reverted and the fix moved to the sidebar's own Latest Story
 * article instead.
 *
 * Thirteenth pass: merchant wants the now-shorter pages filled back out
 * with real editorial content instead of more product ads — GPT-drafted
 * candidate wire stories for the rest of the October 1986 news cycle
 * (Goldwater-Nichols, Bold Guard 86, Display Determination 86, the
 * Stockholm Document, the Daniloff/Zakharov exchange, the NATO fleet
 * schedule, the A-6 training squadron stand-down, the new KC-130 squadron,
 * an SDI intercept test), explicitly excluding the Reykjavik/Iceland summit
 * since that's already this issue's showReykjavik feature. Each spread
 * below carries its own `briefs` array (0-2 entries, each {pos, rot,
 * kicker, head, dek, paras, foot}) instead of the old file-level BRIEFS
 * object + BRIEF_KEYS lookup.
 *
 * Fourteenth pass: merchant flagged (with a screenshot, red-circled) that
 * the thirteenth pass's clippings — a separate rotated box sitting above or
 * below the ad grid — read as stapled-on rather than part of the issue, and
 * pointed at a real masonry gap on that same page as what the stories
 * should instead be filling. Rebuilt: each brief is now spliced straight
 * into that spread's own pg.items array (storyItem() below) as a real grid
 * card — column-span:none, no rotation/shadow — so it's just another piece
 * the browser's own multi-column balance can flow into whichever column has
 * room, the same way an ad card would, instead of fixed outside-the-grid
 * placement.
 *
 * Fifteenth pass: merchant reported real blank spots persisting across most
 * of the issue (pages 1,2,3,4,5,7,8,9,11) — measured live via each page's
 * [data-m="sai-grid"] and found column-height gaps of 280-885px, worst on
 * Page 1. Root cause: the twelfth pass cut every spread's item count from
 * 16 back to 10-12 for page LENGTH, which reopened the exact column-balance
 * problem the eleventh pass's density bump had fixed — capHeroSpan's own
 * medium-first/small-last reorder (assets/sat-import-pager-patch.js) needs
 * enough small items to fine-tune with, and 10-12 is thin for that. The
 * fourteenth pass's story cards were sitting right there as more material
 * but weren't helping: they were unshifted/pushed onto the front/back of
 * pg.items AFTER capHeroSpan already ran, bypassing its shape-based
 * reordering entirely. rebalance() below re-applies that same medium-first/
 * small-last logic to the combined (ads + stories) array — a story card has
 * no adDir, so it already falls into the "small" bucket, and is flagged
 * leanShape so it sorts to the very end of that bucket, the exact "finest
 * filler for whatever a column is still short" slot capHeroSpan's own
 * comment describes. `pos` (top/end) no longer controls final DOM order
 * post-rebalance — it's now just which of a page's two story slots a brief
 * fills, not a literal placement hint.
 *
 * Sixteenth pass: merchant reported blank spots persisting after the
 * fifteenth pass's rebalance() fix — real, but inconsistent (rebalance()
 * helped some pages a lot, left others at 250-400px gaps). A real DOM-level
 * fix (measure each item's actual rendered height, bin-pack into 3 columns,
 * physically reorder the DOM) was prototyped live and DID close gaps near-
 * optimally, but was abandoned: moving real DOM children out from under
 * sat-runtime's own patch()/oldList bookkeeping risks desyncing it on the
 * NEXT re-render (same class of risk as the hydration bug flagged
 * separately), not just a one-time cosmetic glitch. A pure-CSS flex/order
 * replacement for the multi-column container (no DOM moves, just style)
 * was also tried live and rejected — flex-direction:column's flex-basis
 * maps to height, not width, and the column-wrap algorithm didn't honor
 * the precomputed bin assignment, overflowing into extra visual columns.
 * See assets/sat-import-pager-patch.js for how a page's own `size` maps to
 * its grid's real item count: for the two sidebar spreads (K-219, Vanunu)
 * the grid is hard-capped at PAGE2_ITEMS regardless of `size` (the
 * overflow goes to their sidebar filler stack instead), so raising their
 * size can't touch their grid-side gap at all — left alone here. For every
 * other page `size` IS the grid's real item count, so the safe, already-
 * proven lever (more material for rebalance() to balance with, same one
 * the eleventh pass used successfully before the twelfth pass's length cut
 * reopened this) looked like the fix. Tried it on the three worst-measured
 * pages (live gaps, column-bucketed measurement, not the old x-position
 * bucketing that fragmented one real column into several) — Page 1 (345px)
 * 12→14, Mammoth (246px) 10→12, Reykjavik (387px, worst) 10→13. Re-measuring
 * live after deploy: Mammoth and Reykjavik both genuinely improved (246→185,
 * 387→176) and kept. Page 1 got WORSE (348→516px, confirmed on two separate
 * re-measurements, not store drift) — capHeroSpan's medium-first/small-last
 * split apparently has a bad split point at exactly 13 non-hero items;
 * reverted to size 12. Also discovered, and explicitly NOT this pass's to
 * fix: Tax Reform/Blade Room/Closing showed much larger gaps (858/406/394px)
 * than this file's own prior history recorded — confirmed via a live revert
 * test that this is unrelated to the Page 1/Mammoth/Reykjavik size changes
 * above (the bad numbers persisted even with every size reverted to its
 * fifteenth-pass value), so it's real store-content drift (this file's own
 * opening paragraph already documents the store's real card heights
 * drifting between measurements) rather than a regression from this pass —
 * flagged for whoever next re-measures this file's "re-measure before
 * trusting this" numbers, not chased down here. K-219 (161px) and Vanunu
 * (338px) are left at their fifteenth-pass sizes since the lever doesn't
 * reach their grid (hard-capped at PAGE2_ITEMS regardless of size).
 *
 * Seventeenth pass: merchant — Page 1's hero slot (Oscar4Fox's Rhodesian
 * Brushstroke Hooded Combat Shirt) is redundant with the dedicated
 * Oscar4Fox full-page ad later in the issue, and the hero slot's own real
 * product photo (a tall studio portrait, like most of this catalog's
 * garment shots) was badly letterboxed in that slot anyway — the hero shape
 * (assets/sat-import-pager-patch.js, v===0) is a wide, short box
 * (photoMax:420px against a ~670px-wide card), built for a landscape photo,
 * not a portrait one. Swapped the hero pick to the Frogskin Hooded Combat
 * Shirt, a different real product so the two don't read as a duplicate ad,
 * and set that product's own featured image (via Shopify admin, not a
 * theme-side override) to its one landscape flat-lay shot (960×720) instead
 * of a portrait studio shot — the hero slot always uses a product's plain
 * `photo` (its featured image) with no picker involved, see bestPhoto()
 * below, so fixing the actual framing meant fixing which photo leads on the
 * product itself.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }

  var ISSUE = {
    label: 'October 1986 Issue',
    pages: [
      { name: 'Page 1 - ad grid (front-of-book, top priority)', size: 12, gridSize: 12,
        // Sixteenth pass: tried raising to 14 — made Page 1's own gap WORSE
        // (348px → 516px, confirmed via two live re-measurements, not drift) rather
        // than better. capHeroSpan's medium-first/small-last split on 13 items
        // apparently has a bad split point right there; reverted to 12, unlike
        // Mammoth/Reykjavik below which both genuinely improved at their new
        // sizes. Twelfth pass: cut from size:18 to size:12. First landed this with
        // gridSize:10 (2 items held back for page1SidebarFiller, same
        // mechanism the K-219/Vanunu sidebars use) on the old assumption that
        // the GRID was the long side needing something to top it off — but
        // live measurement after the cut showed the opposite: the fixed
        // lore/utility sidebar (8 boxes, including the Latest Story article)
        // was now the long side by nearly 1000px, with the grid well short
        // of it. A grid filler can't shrink the sidebar, so it was pure
        // waste. Reverted to gridSize===size (no filler) and trimmed the
        // Latest Story article itself instead (6 paragraphs → 3, see
        // snippets/sat-import.liquid) — the sidebar's own actual fixed
        // length was the thing to cut, not the grid's.
        picks: [
          'frogskin-hooded-combat-shirt',                               // hero (seventeenth pass — was rhodesian-brushstroke-hooded-combat-shirt, now redundant with the Oscar4Fox full-page ad)
          'rothco-spec-ops-tactical-fleece-jacket',
          'bighorn-high-cut-ballistic-helmet-iiia-coyote-tan',
          'hog-modlite-handheld-package',
          'b-2-spirit-stealth-bomber-legendary-series-mil-blox',
          'sr-71-blackbird-fragment',
          'nasa-chariot-rover-prototype-fragment-verified-authentic-space-artifact',
          'east-german-soldier-boots',
          'rothco-mens-ma-1-flight-jacket-various-colors',
          'an-pvs-14-night-vision-gen-3-white-phosphor-3000-fom-manual-gain-50-hour-runtime',
          'big-boy-vest-xl-combo-package-lightweight-level-iv-2-10x13-5-front-back-plates-2-8x6-side-plates-lvl-iv-ceramic',
          'usgi-intermediate-cold-weather-sleeping-bag'
        ],
        alternates: [
          'german-flecktarn-parka',
          'greek-lizard-m65-field-jacket-w-liner',
          'ray-ban-aviator-sunglasses-for-men',
          'orient-vr32-uab0-mens-quartz-chronograph-with-date-refurbished',
          'issued-french-army-paratrooper-hook-knife',
          'multicam®-combat-pants',
          'streamlight-protac-90x-flashlight',
          'mag-lynx-8-police-law-enforcement-tactical-boot-sz-wp',
          'frogskin-jungle-beach-reversible-woobie-jacket',
          'guerrilla-warfare-and-special-operations-handbook'
        ] },

      { name: 'Spread 1 - Hasenfus: Southern Air Transport, CIA air cargo', size: 10,
        briefs: [
          { pos: 'top', rot: '-.5deg', kicker: 'Wire Brief',
            head: 'Congress Rewrites The Chain Of Command',
            dek: 'Washington — Goldwater-Nichols Act Reshapes The Joint Chiefs',
            paras: [
              'President Reagan signed the Goldwater-Nichols Department of Defense Reorganization Act on October 1st, the broadest overhaul of the U.S. military command structure since the services were unified under one defense establishment in 1947.',
              'The law strengthens the Chairman of the Joint Chiefs of Staff as the President\'s principal military adviser and pushes the services toward genuinely joint operations, answering years of criticism that interservice rivalry had cost lives in recent operations.',
              'Officials say the reorganization will take years to fully implement across the unified commands.'
            ],
            foot: 'SIGNED OCT. 1, 1986 · DEPT. 9' },
          { pos: 'end', rot: '.4deg', kicker: 'Wire Brief',
            head: 'The Intruder\'s Last School',
            dek: 'Whidbey Island, Wash. — Marine A-6 Training Moves To The Navy',
            paras: [
              'Marine All Weather Attack Training Squadron 202 stood down on September 30th after eighteen years training A-6 Intruder pilots and bombardier-navigators.',
              'The training mission transfers to the Navy at NAS Whidbey Island, consolidating A-6 instruction under a single roof.',
              'Veterans of the squadron call it the end of an era for Marine medium attack aviation.'
            ],
            foot: 'STOOD DOWN SEPT. 30, 1986 · DEPT. 9' }
        ],
        picks: [
          'then-came-the-cia-the-early-years-of-southern-air-transport-paperback',
          'surprise-kill-vanish-the-secret-history-of-cia-paramilitary-armies-operators-and-assassins-paperback',
          'skunk-works-a-personal-memoir-of-my-years-of-lockheed-paperback',
          'radar-man-a-personal-history-of-stealth-hardcover',
          'boeing-c-97-stratofreighter-historic-fragment',
          'rothco-vintage-vietnam-fatigue-shirt-rip-stop-with-slant-pockets',
          'u-s-army-military-assistance-command-vietnam-studies-and-observation-group-mac-v-sog-sew-on-agsu-color-patch-each',
          'kore-aviation-p1-headset-with-bag',
          'duffel-bag-green',
          'large-shemagh-tactical-desert-scarf-42-inches-by-42-inches'
        ],
        alternates: [
          'alice-belt-green',
          'kore-aviation-ka-1-headset-with-bag',
          'winter-flyers-helmet-liner-b-9b',
          'asa-ifr-kneeboard-kb-2'
        ] },

      { name: 'Spread 2 - K-219: Soviet sub, naval, nuclear', size: 11,
        // Twelfth pass: cut from 16 to 11 (back near the tenth-pass number,
        // the last one actually confirmed balanced against this sidebar).
        // Re-measure before trusting this — sidebar vs. grid balance is
        // sensitive to exactly this spread's own size.
        briefs: [
          { pos: 'top', rot: '-.6deg', kicker: 'Wire Brief',
            head: 'Can America Shoot Down A Missile?',
            dek: 'White Sands — Pentagon Tests A Ballistic Intercept',
            paras: [
              'A missile-defense experiment conducted in early September used sensors, interceptor rockets and supporting ground equipment to investigate whether an incoming ballistic missile could be tracked and destroyed in flight.',
              'Air Force officials described the test as a significant step for the Strategic Defense Initiative, even as lawmakers in Washington debated the program\'s funding and priorities.',
              'No public scorecard of hits and misses has been released.'
            ],
            foot: 'TESTED EARLY SEPT. 1986 · DEPT. 9' }
        ],
        picks: [
          'the-hunt-for-red-october-hardcover',
          'tom-clancys-jack-ryan-action-pack-the-hunt-for-red-october-the-cardinal-of-the-kremlin-patriot-games-boxed-set',
          '17-90-survival-veshmeshok-v1',
          'cold-war-direct-purchased-russian-military-issued-3rd-gen-6sh117-desert-ksor-digital-camo-tactical-vest',
          'the-woobie-poncho',
          'red-alert-paperback',
          'nuclear-war-a-scenario-hardcover',
          'mira-safety-cm-8m-full-face-respirator-nvg-compatible',
          'nuclear-fallout-honey',
          'from-russia-with-love-a-james-bond-novel-paperback',
          'casino-royale-a-james-bond-novel-paperback'
        ],
        alternates: [
          'east-german-shoulder-boards',
          'east-german-rpg-7-sling',
          'soviet-military-proficiency-badges',
          'east-german-officer-breeches',
          'east-german-tanker-helmet-liner',
          'soviet-gp5-gas-mask-bag',
          'east-german-raincoats',
          'east-german-helmet-nets',
          'soviet-eo-16-gas-mask-filter',
          'soviet-industrial-bkf-gas-mask-filter',
          'east-german-grenztruppen-sleeve-patch'
        ] },

      { name: 'Spread 3 - Mammoth Lakes: Sierra camp in the cold', size: 12,
        // Sixteenth pass: raised from 10 to 12 — see the file-header note.
        briefs: [
          { pos: 'top', rot: '-.4deg', kicker: 'Wire Brief',
            head: '65,000 Men, One Front',
            dek: 'West Germany — NATO Rehearses Reinforcement Of The Northern Flank',
            paras: [
              'NATO\'s Bold Guard 86 exercise ran September 22nd through October 7th across Denmark and West Germany, moving some 65,000 personnel, 2,400 tracked vehicles, 150 helicopters and 150 fixed-wing aircraft.',
              'The main joint operations, held September 22nd through 26th, tested the alliance\'s ability to receive and deploy reinforcements into the Baltic approaches and northern Germany on short notice.',
              'Planners called it one of the largest autumn exercises NATO has staged in years.'
            ],
            foot: 'EXERCISE CONCLUDED OCT. 7, 1986 · DEPT. 9' }
        ],
        picks: [
          'usmc-2-man-combat-shelter-woodland',
          'trekker-tent-2-2-person-backpacking-tent',
          'patrol-sleeping-bag-green',
          'self-inflating-sleeping-mat',
          'the-woobie-blanket-2-0',
          'usgi-canteen-cup-stand-stove',
          'stormproof-matches',
          'titanlight',
          'botas-pro-brown-6-inch-waterproof-hiking-boots-with-vibram®-traction-lug-outsole-bp22121',
          'trekker-palace-tarp-tent'
        ],
        alternates: [
          'map-reading-and-land-navigation-fm-3-25-26-us-army-field-manual-fm-21-26-2001-civilian-reference-edition-unabridged-manual-on-map-use-orienteeri-paperback',
          'hammock-tent',
          'large-field-pack-ocp',
          'mojab-first-aid-kit-151-pieces',
          'nanospark'
        ] },

      { name: 'Spread 4 - Reykjavik: Cold War Europe, wool and winter', size: 13,
        // Sixteenth pass: raised from 10 to 13 (worst-measured gap) — see
        // the file-header note.
        briefs: [
          { pos: 'end', rot: '-.3deg', kicker: 'Wire Brief',
            head: 'The Spy Who Wasn\'t Supposed To Be There',
            dek: 'Washington — Two Detentions End In A Delicate Trade',
            paras: [
              'American correspondent Nicholas Daniloff, held in Moscow on espionage charges since late August, returned to the United States this month after a closely negotiated exchange.',
              'Soviet UN employee Gennadi Zakharov, arrested in New York on espionage charges, was permitted to leave the country after entering a no-contest plea the same week.',
              'Both governments deny any formal trade took place.'
            ],
            foot: 'RESOLVED SEPT. 1986 · DEPT. 9' }
        ],
        picks: [
          'stasi-the-untold-story-of-the-east-german-secret-police-paperback',
          'secret-empire-eisenhower-the-cia-and-the-hidden-story-of-americas-space-espionage-paperback',
          'polish-wz-89-puma-backpack',
          'romanian-m1994-spotted-camo-field-jacket',
          'german-bundeswehr-olive-fleece-jacket',
          'austrian-bundesheer-kaz-75-field-pants-like-new',
          'tw-kempton-york-woolly-pully-vee-neck-sweater-with-patches',
          'swiss-taz-90-field-pants',
          'polish-wz-93-field-jacket',
          'french-wool-ike-jacket'
        ],
        alternates: [
          'moonraker-a-james-bond-novel-paperback',
          'dutch-commando-wool-sweater-emerald-blue',
          'west-german-sleeveless-wool-vest-modified',
          'swedish-m39-rucksack',
          'austrian-bundesheer-tarnanzug-neu-field-pants'
        ] },

      { name: 'Spread 5 - Vanunu: Israeli-made kit, Middle East, atomic', size: 12,
        // Twelfth pass: cut from 19 to 12.
        briefs: [
          { pos: 'top', rot: '.5deg', kicker: 'Wire Brief',
            head: 'The Mediterranean Front',
            dek: 'Southern Europe — NATO Rehearses The War Nobody Wants To Fight',
            paras: [
              'NATO\'s Display Determination 86 exercise ran from September 19th through October 13th across the Mediterranean theater, with more than 2,000 U.S. Marines joined by 800 Turkish and Italian troops.',
              'The Marine component practiced combined amphibious landings alongside allied forces, rehearsing the alliance\'s southern-flank response.',
              'It is the Mediterranean counterpart to the larger Bold Guard exercise further north.'
            ],
            foot: 'EXERCISE CONCLUDED OCT. 13, 1986 · DEPT. 9' }
        ],
        picks: [
          'targeted-beirut-the-1983-marine-barracks-bombing-and-the-untold-origin-story-of-the-war-on-terror-hardcover',
          'aladdin-synchrotron-particle-accelerator-fragment-authentic-cooling-coil-section',
          'rt-archangel-2-0carrier-assault-flatpack-combo-with-advanced-padding',
          'rt-seige-2-0-full-coverage-m-s-o-v-carrier-ballistic-vest',
          'bullet-proof-vest-level-iiia',
          'ballistic-helmet-with-face-shield-nij-level-iiia',
          'full-plate-carrier-that-fits-front-plates-and-side-plates',
          'tactical-molle-holster',
          'lightweight-battle-belt-harness',
          'tactical-thigh-gas-mask-pouch',
          'submachine-gun-case',
          'blackhawk-430604bk-r-pistol-holster'
        ],
        alternates: [
          'warden-chest-holster',
          'shotgun-belt-holds-25-shotshells',
          'co-shooters-belt-with-cobra-buckle',
          'elite-cobra-riggers-belt-with-d-ring-buckle',
          'belt-slide-cartridge-carriers',
          'securedraw-gun-magnet',
          'utility-belt',
          'redemption-tactical-helmet-bag',
          'pair-of-26605-nij-certified-swimmers-cut-22605-2-10x12-level-iv-ballistic-plates'
        ] },

      { name: 'Spread 6 - Tax Reform: the bargain page, all under $20', size: 10,
        briefs: [
          { pos: 'top', rot: '.3deg', kicker: 'Wire Brief',
            head: 'Now They Have To Tell Us',
            dek: 'Stockholm — East And West Open The Curtain On War Games',
            paras: [
              'Thirty-five CSCE member states adopted a new set of confidence- and security-building measures on military activity in Europe on September 19th, including advance notice of major exercises and new observation and inspection rights.',
              'The measures are politically binding and are scheduled to take effect January 1, 1987.',
              'Diplomats call it the most concrete product of the Helsinki process in years.'
            ],
            foot: 'ADOPTED SEPT. 19, 1986 · DEPT. 9' },
          { pos: 'end', rot: '-.5deg', kicker: 'Wire Brief',
            head: 'NATO\'s October War Games',
            dek: 'Baltic To Black Sea — The Fleet Keeps Moving',
            paras: [
              'A string of NATO naval exercises ran through the Mediterranean and European waters this fall: Display Determination (Sept. 19–Oct. 13), Damsel Fair 8612 (Sept. 29–Oct. 11), BaltOps 87 (Oct. 1–17), Black Sea Ops 87-1 (Oct. 16–21) and Deterrent Force 2186 (Oct. 15–Nov. 17).',
              'Taken together, the schedule keeps allied fleets continuously exercised from the Baltic approaches to the Black Sea through the end of the year.'
            ],
            foot: 'ONGOING THROUGH NOV. 1986 · DEPT. 9' }
        ],
        picks: [
          'small-arms-ammo-pouch-olive',
          'pouch-general-purpose-black-alice',
          'us-gi-od-1qt-canteen-used',
          'ka-bar-tactical-spork',
          'dagr-case-military-gps-pouch',
          'east-german-volkspolizie-traffic-police-jacket',
          'german-military-rubber-overboots',
          'israeli-canvas-grenade-pouch',
          'french-gas-mask-bag',
          'swedish-canteen'
        ],
        alternates: [
          'us-lce-m56-field-suspenders',
          'usmc-coyote-tan-entrenching-tool-carrier-pouch-issued-condition',
          'east-german-1960s-80s-1-pfennig-coin',
          'spanish-military-trouser-shorts-modified'
        ] },

      { name: 'Spread 7 - The Blade Room: knives, kukris and tomahawks', size: 10,
        tab: 'THE BLADE ROOM',
        briefs: [
          { pos: 'top', rot: '-.5deg', kicker: 'The Blade Room',
            head: 'Kukris, Bowies And Tomahawks, Hand-Forged And On The Shelf',
            dek: 'Dept. 9 — Camp, Bushcraft And Collector Steel',
            paras: [
              'This month the back of the book gives a full page to steel: Nepalese-pattern kukris, Damascus and san mai Bowies, and carved-handle tomahawks from the smiths who supply our counter.',
              'Built for the camp, the woodpile and the wall. Check your local laws before ordering.',
              'Faults are listed, not argued. Write Dept. 9 about anything you want a closer look at.'
            ],
            foot: 'OCT. 1986 · DEPT. 9' },
          { pos: 'end', rot: '.4deg', kicker: 'Wire Brief',
            head: 'The Air Bridge To The Battlefield',
            dek: 'Cherry Point, N.C. — A New Squadron Stands Up For Marine Refueling',
            paras: [
              'The Marine Corps activated Marine Aerial Refueler Transport Squadron 253 at MCAS Cherry Point on October 1st, standing up with six KC-130 tanker-transport aircraft.',
              'The squadron extends the Marines\' tactical air-refueling and transport reach, the same logistics architecture that keeps an amphibious force supplied once it is ashore.'
            ],
            foot: 'ACTIVATED OCT. 1, 1986 · DEPT. 9' }
        ],
        picks: [
          'copper-san-mai-damascus-bowie-with-clip-point-blade',
          'handmade-frontier-fang-kukri-knife-12-with-natural-bone-handle',
          'crown-stag-kukri-bowie-knife-with-layered-resin-handle',
          'handcrafted-western-bowie-knife-clip-point-hunting-blade-with-brass-guard-and-leather-sheath',
          'fallkniven-garm-fighter-fixed-blade-knife',
          'the-ravager-damascus-gut-hook-hunting-knife-tk-400',
          'titan-warden-damascus-steel-stacked-leather-handle-tk-117',
          'damascus-steel-outdoor-chopping-tool-obsidian-dark-wenga-wood-handle-tk-126',
          'eagles-valor-damascus-by-titan-tk-116'
        ],
        alternates: [
          'custom-fixed-blade-bowie-knife-leather-grip-brass-clip-sheath',
          'lunar-guardian-damascus-knife-tk-098-compact',
          'titan-ranger-hunting-blade-to-002'
        ] },

      { name: 'Closing page - back of book: Halloween, fallout and gifts', size: 10,
        picks: [
          'resident-evil-inspired-leon-kennedy-re2-embroidered-sweatshirt-double-sided-horror-game-merch',
          'fallout-the-soothing-sounds-of-the-apocalypse-vinyl-soundtrack',
          'jones-fallout-series-limited-edition',
          'f-16-falcon-legendary-series-mil-blox',
          'f-22-raptor-legendary-series-mil-blox',
          'the-woobie-hoodie',
          'silkies-ranger-panties',
          'metal-gear-solid-vinyl-selections-soundtrack',
          'army-pt-jacket',
          'vogue-0vo4356s-mens-square-sunglasses-gunmetal-grey-57mm'
        ],
        alternates: [
          'woobie-jacket-2-0',
          'ch-47-chinook-legendary-series-mil-blox',
          'howitzer-cannon-mil-blox',
          'savage-son-a-thriller-hardcover'
        ] }
    ]
  };
  window.SAT_ISSUE = ISSUE;

  var EXPLICIT_SORTS = ['Price, Low to High', 'Price, High to Low', 'Oldest Era'];

  function curatedActive(st) {
    if (!st) return false;
    if (st.page !== 'home' && st.page !== 'store') return false;
    if (String(st.query || '').trim()) return false;
    if (st.activeCat && st.activeCat !== 'All') return false;
    if (EXPLICIT_SORTS.indexOf(st.sortBy) !== -1) return false;
    return true;
  }

  var proto = window.SATComponent.prototype;
  var origVisibleProducts = proto.visibleProducts;
  proto.visibleProducts = function () {
    var list = origVisibleProducts.call(this);
    if (!curatedActive(this.state)) return list;

    var all = (this._products && this._products.length) ? this._products : list;
    var byHandle = {};
    all.forEach(function (p) { if (p && p.handle && !byHandle[p.handle]) byHandle[p.handle] = p; });

    var used = {};
    function free(p) { return p && p.id != null && !used[p.id]; }
    function inStock(p) { return p && p.stock > 0; }
    function lookup(handles) {
      return (handles || []).map(function (h) { return byHandle[h]; }).filter(Boolean);
    }
    // Deterministic backstop, only used if a page runs out of its own picks and alternates.
    var reserve = all.filter(inStock).slice().sort(function (a, b) {
      return String(a.handle) < String(b.handle) ? -1 : (String(a.handle) > String(b.handle) ? 1 : 0);
    });

    var out = [];
    ISSUE.pages.forEach(function (pg) {
      var page = [];
      function take(p) { if (page.length < pg.size && free(p) && inStock(p)) { page.push(p); used[p.id] = true; } }
      lookup(pg.picks).forEach(take);
      lookup(pg.alternates).forEach(take);
      reserve.forEach(take);
      out = out.concat(page);
    });
    return out.length ? out : list;
  };

  // A story card shares the outer per-item wrapper (column-span/border/skew)
  // with a real ad card — see snippets/sat-import.liquid's sc-for over
  // pg.items — but renders dry-wire text instead of a photo/price/button.
  // columnSpan:'none' so it just takes one column slot in the flow, same as
  // any single-column ad shape; no adSkew/rotation, since this should read
  // as a real page element, not a pinned clipping. leanShape:true and no
  // adDir puts it in rebalance()'s "small" bucket, sorted to the very end —
  // the finest-grained filler material for topping off a short column.
  function storyItem(b, key) {
    return {
      isStory: true, leanShape: true, id: 'brief-' + key,
      columnSpan: 'none', rule: '1px', edgeFade: '.68', adSkew: 'none', inkDensity: 1,
      kicker: b.kicker, head: b.head, dek: b.dek, paras: b.paras, foot: b.foot
    };
  }

  // Same medium-first/small-last column-balance reorder capHeroSpan applies
  // in assets/sat-import-pager-patch.js, re-run here over the combined
  // ads + stories array so the inserted story cards actually participate in
  // it instead of just being stuck on the front/back of an already-ordered
  // list. The true hero (columnSpan:'all') is found by scanning rather than
  // assumed to be items[0], since a top-positioned story can now sit ahead
  // of it in the raw array.
  function rebalance(items) {
    if (items.length < 2) return items;
    var heroIdx = -1;
    for (var i = 0; i < items.length; i++) { if (items[i].columnSpan === 'all') { heroIdx = i; break; } }
    var hero = heroIdx === -1 ? [] : [items[heroIdx]];
    var rest = items.filter(function (it, i) { return i !== heroIdx; });
    var medium = rest.filter(function (it) { return it.adDir === 'column'; });
    var small = rest.filter(function (it) { return it.adDir !== 'column'; });
    small.sort(function (a, b) { return (a.leanShape ? 1 : 0) - (b.leanShape ? 1 : 0); });
    return hero.concat(medium, small);
  }

  var origRenderVals = proto.renderVals;
  window.SATComponent.prototype.renderVals = function () {
    var out = origRenderVals.call(this);
    if (Array.isArray(out.saiMiddlePages)) {
      out.saiMiddlePages = out.saiMiddlePages.map(function (pg, idx) {
        // ISSUE.pages[0] is page 1; spreads follow in order, so spread idx is pages[idx + 1].
        var def = ISSUE.pages[idx + 1] || {};
        var briefs = def.briefs || [];
        var o = Object.assign({}, pg);
        if (def.tab) { o.tabLabel = def.tab; }
        // assets/sat-import-pager-patch.js (loads before this file) already
        // packed pg.items/pg.gridStyle for a real column-balanced grid, height
        // included (window.SATImportColumnPack) — see its own comments. Only
        // worth redoing here when a brief is actually being added, since
        // that's the only thing that can make the already-packed height
        // stale (a changed ITEM SET, not just a reorder); a spread with no
        // briefs is left exactly as that patch produced it.
        if (!briefs.length) { return o; }
        var items = (pg.items || []).slice();
        briefs.forEach(function (b, bi) { items.push(storyItem(b, idx + '-' + bi)); });
        if (window.SATImportColumnPack) {
          var packed = window.SATImportColumnPack.packColumns(items);
          o.items = packed.ordered;
          o.gridStyle = window.SATImportColumnPack.gridStyle(packed.height);
        } else {
          o.items = rebalance(items);
        }
        return o;
      });
    }
    return out;
  };
})();
